import React, { useEffect, useRef, useState } from 'react';
import { X, Upload } from 'lucide-react';

interface KetcherModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (smiles: string, molfile: string) => void;
  initialStructure?: string;
}

/**
 * KetcherModal Component
 * 
 * Modal popup for Ketcher chemical structure editor.
 * Provides:
 * - Full-screen Ketcher editor
 * - Import structure from SMILES/MOLfile
 * - Export structure back to parent
 */
export const KetcherModal: React.FC<KetcherModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialStructure,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importValue, setImportValue] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [ketcherWindow, setKetcherWindow] = useState<Window | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      // Close popup if modal is closed
      if (ketcherWindow && !ketcherWindow.closed) {
        ketcherWindow.close();
        setKetcherWindow(null);
      }
      return;
    }

    // Open Ketcher in a popup window instead of iframe (to avoid X-Frame-Options)
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    const popup = window.open(
      'https://lifescience.opensource.epam.com/KetcherDemoSA/index.html',
      'KetcherEditor',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
    );

    if (!popup) {
      setError('Popup blocked. Please allow popups for this site.');
      setIsLoading(false);
      return;
    }

    setKetcherWindow(popup);
    setIsLoading(true);
    setError(null);

    // Listen for messages from popup (if Ketcher supports it)
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from Ketcher origin
      if (event.origin !== 'https://lifescience.opensource.epam.com') return;

      if (event.data && event.data.type === 'ketcher-structure') {
        const { smiles, molfile } = event.data;
        if (smiles || molfile) {
          onSave(smiles || '', molfile || '');
          onClose();
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Check if popup is closed
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        setIsLoading(false);
        setKetcherWindow(null);
        clearInterval(checkClosed);
      } else {
        // Popup is open
        setIsLoading(false);
      }
    }, 500);

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(checkClosed);
      if (popup && !popup.closed) {
        popup.close();
      }
    };
  }, [isOpen, retryKey]);

  const handleImport = () => {
    if (!importValue.trim()) {
      alert('Please enter a SMILES string or MOLfile');
      return;
    }

    // Try to detect if it's SMILES or MOLfile
    const isMolfile = importValue.includes('M  END') || importValue.split('\n').length > 5;
    
    if (isMolfile) {
      // MOLfile format - save directly
      onSave('', importValue.trim());
      alert('MOLfile imported successfully!');
    } else {
      // SMILES format - save directly
      onSave(importValue.trim(), '');
      alert('SMILES imported successfully!');
    }

    setShowImport(false);
    setImportValue('');
    onClose();
  };

  const handleSave = () => {
    if (!ketcherWindow || ketcherWindow.closed) {
      alert('Please open the Ketcher editor first by allowing the popup.');
      return;
    }

    const instructions = 
      'To save structure:\n\n' +
      '1. In the Ketcher popup window, draw or edit your structure\n' +
      '2. Click "Copy SMILES" button in Ketcher\'s toolbar\n' +
      '3. Return here and click "OK" below\n' +
      '4. Paste the SMILES string when prompted\n\n' +
      (initialStructure 
        ? `Current structure: ${initialStructure}\n\n` 
        : '') +
      'Note: You can also import SMILES using the Import button above.';

    alert(instructions);

    const smiles = prompt('Paste the SMILES string from Ketcher:');
    if (smiles && smiles.trim()) {
      // For MOLfile, user would need to copy it separately
      const molfile = prompt('Paste the MOLfile (optional, press Cancel to skip):') || '';
      onSave(smiles.trim(), molfile.trim());
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h3 style={styles.title}>Chemical Structure Editor</h3>
          <div style={styles.headerActions}>
            <button
              style={styles.importButton}
              onClick={() => setShowImport(!showImport)}
              title="Import structure"
            >
              <Upload size={16} />
              <span>Import</span>
            </button>
            <button style={styles.closeButton} onClick={onClose} title="Close">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Import Section */}
        {showImport && (
          <div style={styles.importSection}>
            <label style={styles.label}>
              Import Structure (SMILES or MOLfile)
            </label>
            <textarea
              style={styles.importTextarea}
              value={importValue}
              onChange={(e) => setImportValue(e.target.value)}
              placeholder="Paste SMILES string (e.g., CCO) or MOLfile content here..."
              rows={4}
            />
            <div style={styles.importActions}>
              <button style={styles.importSubmitButton} onClick={handleImport}>
                Import
              </button>
              <button
                style={styles.importCancelButton}
                onClick={() => {
                  setShowImport(false);
                  setImportValue('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Ketcher Container */}
        <div style={styles.ketcherContainer}>
          {isLoading && !error && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={styles.loadingText}>Opening Ketcher editor in popup...</p>
              <p style={styles.loadingSubtext}>
                If popup doesn't open, check your browser's popup blocker settings
              </p>
            </div>
          )}
          {error && (
            <div style={styles.errorContainer}>
              <p style={styles.errorText}>{error}</p>
              <button
                style={styles.retryButton}
                onClick={() => {
                  setError(null);
                  setRetryKey((prev) => prev + 1);
                }}
              >
                Retry
              </button>
            </div>
          )}
          {ketcherWindow && !ketcherWindow.closed && !error && (
            <div style={styles.popupInfo}>
              <div style={styles.popupIcon}>✓</div>
              <div style={styles.popupContent}>
                <p style={styles.popupTitle}>Ketcher Editor Opened</p>
                <p style={styles.popupText}>
                  The Ketcher editor has been opened in a new window.
                  <br />
                  Draw your structure, then click "Copy SMILES" in Ketcher.
                  <br />
                  Return here and click "Save Structure" below.
                </p>
                {initialStructure && (
                  <p style={styles.popupHint}>
                    Tip: Paste this SMILES in Ketcher to load: <code>{initialStructure}</code>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <div style={styles.footerInfo}>
            <p style={styles.footerText}>
              Draw your structure above, then click "Copy SMILES" in Ketcher's toolbar
            </p>
          </div>
          <div style={styles.footerActions}>
            <button style={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button style={styles.saveButton} onClick={handleSave}>
              Save Structure
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    width: '90vw',
    height: '90vh',
    maxWidth: '1200px',
    maxHeight: '800px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#1e293b',
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  importButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  closeButton: {
    padding: '6px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  importSection: {
    padding: '16px 20px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#334155',
  },
  importTextarea: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '13px',
    fontFamily: 'monospace',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  importActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '12px',
  },
  importSubmitButton: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  importCancelButton: {
    padding: '8px 16px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  ketcherContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  },
  popupInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '32px',
    backgroundColor: '#f0fdf4',
    border: '2px solid #bbf7d0',
    borderRadius: '8px',
    maxWidth: '500px',
    textAlign: 'center',
  },
  popupIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    backgroundColor: '#16a34a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: 'bold',
  },
  popupContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  popupTitle: {
    margin: 0,
    fontSize: '18px',
    fontWeight: 600,
    color: '#166534',
  },
  popupText: {
    margin: 0,
    fontSize: '14px',
    color: '#15803d',
    lineHeight: '1.6',
  },
  popupHint: {
    margin: '12px 0 0 0',
    padding: '8px 12px',
    fontSize: '12px',
    color: '#166534',
    backgroundColor: '#fff',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  retryButton: {
    marginTop: '16px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '14px',
    color: '#64748b',
    fontWeight: 500,
  },
  loadingSubtext: {
    marginTop: '8px',
    fontSize: '12px',
    color: '#94a3b8',
  },
  errorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fee2e2',
    padding: '20px',
  },
  errorText: {
    fontSize: '14px',
    color: '#991b1b',
    textAlign: 'center',
  },
  footer: {
    padding: '16px 20px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerInfo: {
    flex: 1,
  },
  footerText: {
    margin: 0,
    fontSize: '12px',
    color: '#64748b',
  },
  footerActions: {
    display: 'flex',
    gap: '8px',
  },
  cancelButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#64748b',
    backgroundColor: '#f1f5f9',
    border: '1px solid #cbd5e1',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#fff',
    backgroundColor: '#2563eb',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
};

// Add spinner animation
if (typeof document !== 'undefined') {
  const existingStyle = document.getElementById('ketcher-modal-spinner-style');
  if (!existingStyle) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'ketcher-modal-spinner-style';
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(styleSheet);
  }
}

